import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CATEGORIES, type Category } from "@/lib/questions";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/usePremium";
import { listAdmins, grantAdminByEmail, revokeAdmin, claimFirstAdmin } from "@/lib/admins.functions";
import { listAllQuestions, upsertQuestion, deleteQuestion } from "@/lib/questions.functions";
import { invalidateQuestionsCache } from "@/hooks/useQuestions";
import { ShieldAlert, Pencil, Plus, Loader2, Trash2, ShieldCheck } from "lucide-react";
import { ContactMessagesSection } from "@/components/admin/ContactMessagesSection";
import { UserStatsSection } from "@/components/admin/UserStatsSection";
import { WelcomeBackfillSection } from "@/components/admin/WelcomeBackfillSection";
import { SeoMonitorSection } from "@/components/admin/SeoMonitorSection";
import { ServiceLeadsSection } from "@/components/admin/ServiceLeadsSection";

export const Route = createFileRoute("/admin")({
  component: Admin,
  head: () => ({
    meta: [
      { title: "Adminpanel – Alkoholprovet" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type EditQ = {
  id?: string;
  category: Category;
  question: string;
  options: string[];
  correct: number;
  explanation: string;
  active: boolean;
  sort_order: number;
};

function blankQ(): EditQ {
  return {
    category: CATEGORIES[0],
    question: "",
    options: ["", "", "", ""],
    correct: 0,
    explanation: "",
    active: true,
    sort_order: 0,
  };
}

function Admin() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading, refresh } = useIsAdmin(user?.id);
  const [editing, setEditing] = useState<EditQ | null>(null);

  const claim = useServerFn(claimFirstAdmin);
  const [claiming, setClaiming] = useState(false);
  const [claimErr, setClaimErr] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) nav({ to: "/login" });
  }, [authLoading, user, nav]);

  if (authLoading || roleLoading) {
    return <div className="container-page py-20 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  }

  if (!isAdmin) {
    return (
      <div className="container-narrow py-16 text-center">
        <ShieldAlert className="h-10 w-10 mx-auto text-warning" />
        <h1 className="mt-4 font-display text-2xl font-bold">Endast administratörer</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Du har inte adminbehörighet. Om ingen admin är tilldelad ännu kan du göra dig själv till första admin.
        </p>

        <button
          disabled={claiming}
          onClick={async () => {
            setClaimErr(null); setClaiming(true);
            try { await claim(); await refresh?.(); }
            catch (e: any) { setClaimErr(e?.message ?? "Kunde inte tilldela adminroll."); }
            finally { setClaiming(false); }
          }}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Gör mig till första admin
        </button>
        {claimErr && <p className="mt-3 text-sm text-destructive">{claimErr}</p>}

        <div className="mt-6">
          <Link to="/" className="text-sm text-muted-foreground hover:underline">Till startsidan</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-page py-10 space-y-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Adminpanel</h1>
          <p className="text-sm text-muted-foreground">Hantera frågor och administratörer.</p>
        </div>
        <button
          onClick={() => setEditing(blankQ())}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Ny fråga
        </button>
      </div>

      <SeoMonitorSection />

      <UserStatsSection />

      <AdminsSection currentUserId={user!.id} />

      <ServiceLeadsSection />

      <ContactMessagesSection />

      <WelcomeBackfillSection />

      <QuestionsSection onEdit={setEditing} />

      {editing && (
        <EditorModal
          value={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function QuestionsSection({ onEdit }: { onEdit: (q: EditQ) => void }) {
  const list = useServerFn(listAllQuestions);
  const del = useServerFn(deleteQuestion);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Category | "Alla">("Alla");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-questions"],
    queryFn: () => list(),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      invalidateQuestionsCache();
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
    },
  });

  const rows = (data ?? []).filter((q: any) => filter === "Alla" || q.category === filter);

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="font-display text-xl font-semibold">Frågor ({data?.length ?? 0})</h2>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
        >
          <option>Alla</option>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-card">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Laddar frågor…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>
                <th className="px-4 py-3">Kategori</th>
                <th className="px-4 py-3">Fråga</th>
                <th className="px-4 py-3 w-40 text-right">Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((q: any) => (
                <tr key={q.id} className="border-t border-border">
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap align-top">{q.category}</td>
                  <td className="px-4 py-3 align-top">{q.question}</td>
                  <td className="px-4 py-3 text-right align-top whitespace-nowrap">
                    <button
                      onClick={() => onEdit({
                        id: q.id,
                        category: q.category,
                        question: q.question,
                        options: Array.isArray(q.options) ? q.options : [],
                        correct: q.correct,
                        explanation: q.explanation ?? "",
                        active: q.active,
                        sort_order: q.sort_order ?? 0,
                      })}
                      className="inline-flex items-center gap-1 text-accent hover:underline mr-3"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Redigera
                    </button>
                    <button
                      onClick={() => { if (confirm("Ta bort frågan?")) delMut.mutate(q.id); }}
                      className="inline-flex items-center gap-1 text-destructive hover:underline"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Ta bort
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-muted-foreground">Inga frågor.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function EditorModal({ value, onClose }: { value: EditQ; onClose: () => void }) {
  const [q, setQ] = useState<EditQ>(value);
  const [err, setErr] = useState<string | null>(null);
  const upsert = useServerFn(upsertQuestion);
  const qc = useQueryClient();

  const saveMut = useMutation({
    mutationFn: () => upsert({ data: q as any }),
    onSuccess: () => {
      invalidateQuestionsCache();
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      onClose();
    },
    onError: (e: any) => setErr(e?.message ?? "Kunde inte spara."),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl w-full max-w-lg p-6 shadow-elevated max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-xl font-semibold mb-4">{q.id ? "Redigera fråga" : "Ny fråga"}</h2>
        <div className="space-y-3">
          <select value={q.category} onChange={(e) => setQ({ ...q, category: e.target.value as Category })} className="w-full rounded-lg border border-input bg-background px-3 py-2">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea value={q.question} onChange={(e) => setQ({ ...q, question: e.target.value })} placeholder="Frågetext" className="w-full rounded-lg border border-input bg-background px-3 py-2" rows={2} />
          {q.options.map((o, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input type="radio" checked={q.correct === i} onChange={() => setQ({ ...q, correct: i })} />
              <input value={o} onChange={(e) => { const opts = [...q.options]; opts[i] = e.target.value; setQ({ ...q, options: opts }); }} placeholder={`Alternativ ${String.fromCharCode(65 + i)}`} className="flex-1 rounded-lg border border-input bg-background px-3 py-2" />
            </div>
          ))}
          <textarea value={q.explanation} onChange={(e) => setQ({ ...q, explanation: e.target.value })} placeholder="Förklaring" className="w-full rounded-lg border border-input bg-background px-3 py-2" rows={2} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={q.active} onChange={(e) => setQ({ ...q, active: e.target.checked })} />
            Aktiv (synlig för användare)
          </label>
        </div>
        {err && <p className="mt-3 text-sm text-destructive">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm">Avbryt</button>
          <button
            disabled={saveMut.isPending}
            onClick={() => { setErr(null); saveMut.mutate(); }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminsSection({ currentUserId }: { currentUserId: string }) {
  const list = useServerFn(listAdmins);
  const grant = useServerFn(grantAdminByEmail);
  const revoke = useServerFn(revokeAdmin);
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const { data: admins, isLoading } = useQuery({
    queryKey: ["admins"],
    queryFn: () => list(),
  });

  const grantMut = useMutation({
    mutationFn: (e: string) => grant({ data: { email: e } }),
    onSuccess: () => { setEmail(""); setErr(null); qc.invalidateQueries({ queryKey: ["admins"] }); },
    onError: (e: any) => setErr(e?.message ?? "Kunde inte lägga till admin."),
  });

  const revokeMut = useMutation({
    mutationFn: (uid: string) => revoke({ data: { user_id: uid } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }),
    onError: (e: any) => setErr(e?.message ?? "Kunde inte ta bort admin."),
  });

  return (
    <section>
      <h2 className="font-display text-xl font-semibold mb-3">Administratörer</h2>
      <div className="rounded-2xl border border-border bg-card p-5 shadow-card space-y-4">
        <form
          onSubmit={(e) => { e.preventDefault(); if (email) grantMut.mutate(email); }}
          className="flex flex-col sm:flex-row gap-2"
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ny.admin@exempel.se"
            className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={grantMut.isPending}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {grantMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Tilldela admin
          </button>
        </form>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <p className="text-xs text-muted-foreground">Användaren måste först ha registrerat ett konto.</p>

        <div className="border-t border-border pt-4">
          {isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Laddar…</div>
          ) : (
            <ul className="divide-y divide-border">
              {admins?.map((a) => (
                <li key={a.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <div className="text-sm font-medium">{a.email ?? "(okänd e-post)"}</div>
                    <div className="text-xs text-muted-foreground">Tilldelad {new Date(a.created_at).toLocaleDateString("sv-SE")}</div>
                  </div>
                  {a.user_id !== currentUserId && (
                    <button
                      onClick={() => { if (confirm(`Ta bort admin för ${a.email}?`)) revokeMut.mutate(a.user_id); }}
                      className="inline-flex items-center gap-1 text-sm text-destructive hover:underline"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Ta bort
                    </button>
                  )}
                </li>
              ))}
              {admins && admins.length === 0 && <li className="py-2 text-sm text-muted-foreground">Inga administratörer ännu.</li>}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
